/**
 * RelationshipPanel - Detail panel for a selected edge (relationship).
 * Shows tabs: Overview | Co-Mentions | Interactions
 */

import React, { useState } from 'react';
import { Box, Typography, Avatar, IconButton, Tabs, Tab } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { ZONE_CONFIG } from '../../constants/dashboardConfig';
import { getInitials } from '../../utils/networkUtils';
import RelOverviewTab from './RelOverviewTab';
import RelCoMentionsTab from './RelCoMentionsTab';
import RelInteractionsTab from './RelInteractionsTab';

function RelationshipPanel({ edge, entityId, nodes, onClose }) {
  const [tabValue, setTabValue] = useState(0);

  // Find source and target nodes
  const sourceNode = nodes?.find((n) => n.id === edge.source) || { id: edge.source, label: edge.source };
  const targetNode = nodes?.find((n) => n.id === edge.target) || { id: edge.target, label: edge.target };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* Header */}
      <Box
        sx={{
          p: 2.5,
          background: 'linear-gradient(135deg, #100331 0%, #1a0845 100%)',
          color: 'white',
          flexShrink: 0,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="subtitle2" sx={{ color: 'rgba(201,160,220,1)' }}>
            Relationship
          </Typography>
          <IconButton onClick={onClose} sx={{ color: 'white' }} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar
            sx={{
              width: 40,
              height: 40,
              bgcolor: ZONE_CONFIG[sourceNode.zone]?.color || '#767676',
              fontSize: '0.9rem',
              fontWeight: 700,
            }}
          >
            {getInitials(sourceNode.label)}
          </Avatar>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>
              connected to
            </Typography>
          </Box>
          <Avatar
            sx={{
              width: 40,
              height: 40,
              bgcolor: ZONE_CONFIG[targetNode.zone]?.color || '#767676',
              fontSize: '0.9rem',
              fontWeight: 700,
            }}
          >
            {getInitials(targetNode.label)}
          </Avatar>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {sourceNode.label}
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {targetNode.label}
          </Typography>
        </Box>
      </Box>

      {/* Tabs */}
      <Tabs
        value={tabValue}
        onChange={(e, v) => setTabValue(v)}
        sx={{
          minHeight: 40,
          flexShrink: 0,
          '& .MuiTab-root': { minHeight: 40, fontSize: '0.75rem', textTransform: 'none' },
        }}
      >
        <Tab label="Overview" />
        <Tab label="Co-Mentions" />
        <Tab label="Interactions" />
      </Tabs>

      {/* Tab Content */}
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {tabValue === 0 && (
          <RelOverviewTab edge={edge} sourceNode={sourceNode} targetNode={targetNode} entityId={entityId} />
        )}
        {tabValue === 1 && (
          <RelCoMentionsTab sourceNode={sourceNode} targetNode={targetNode} entityId={entityId} />
        )}
        {tabValue === 2 && (
          <RelInteractionsTab sourceNode={sourceNode} targetNode={targetNode} entityId={entityId} />
        )}
      </Box>
    </Box>
  );
}

export default RelationshipPanel;
